// Ionic & Angular Imports
import {Component, Renderer, ViewChild, ElementRef} from '@angular/core';
import {FormBuilder, Validators, FormGroup} from '@angular/forms';
import {Events, AlertController, PopoverController, ViewController, 
        NavParams, LoadingController, Loading} from 'ionic-angular';

// Service Imports
import {rsReportSvc} from '../../../../../services/report.service';
import {rsPhotoSvc, Photo} from '../../../../../services/photo.service';

// Component Imports
import {BasePage} from '../../../../base.component';

@Component({
    templateUrl: 'picture-modal.html',
    providers: [
        rsPhotoSvc
    ]
})
export class PictureModal extends BasePage
{
    // Class member variables
    private modalForm       : FormGroup;
    private editMode        : boolean;
    private initPhoto       : Photo;

    @ViewChild('imageInput') imageInput : ElementRef;
    private imageFile       : File;
    private imageUrl        : string = "";

    constructor(
        protected alertCtrl : AlertController,
        private navParams : NavParams, 
        private reportSvc: rsReportSvc, 
        private photoSvc: rsPhotoSvc,
        private loadingCtrl : LoadingController,
        private viewCtrl : ViewController,
        private formBuilder : FormBuilder,
        private popupCtrl : PopoverController,
        private eventSystem : Events,
        private renderer : Renderer
    ){
        // Construct base class
        super( alertCtrl );

        // Retrieve modal data from NavParams           
        this.editMode = this.navParams.get("editmode");
        if( this.editMode ) { 
            this.initPhoto = <Photo>this.navParams.get("photo");
            this.imageUrl = 'http://dfr.hydrocon.com/' + this.initPhoto.thumb;

        } else {
            // Initialize our activity object
            this.imageUrl = 'assets/images/placeholder.jpg';
            this.initPhoto = <Photo>{
                id: 0,
                image: "",
                thumb: "",
                notes: "",
            };
        }

        // Build our form model structure
        this.buildFormModel();
    }

    // Class method for builidng our form model structure 
    private buildFormModel() {
        this.modalForm = this.formBuilder.group( {
            image:         [this.initPhoto.image, Validators.required],
            details:       [this.initPhoto.notes]
        });
    }

    // Class helper method for setting a field in the form to a specified value
    private setFormField( fieldName: string, fieldValue: any ) {
        this.modalForm.controls[fieldName].setValue( fieldValue );
        this.modalForm.controls[fieldName].markAsDirty();
    }

    // Class helper method for opening the 'SelectFile' dialog box when the input is hidden
    public selectImageDialog( event : EventTarget ) {
        let evt = new MouseEvent("click", {bubbles: true});
        this.renderer.invokeElementMethod( this.imageInput.nativeElement, 'dispatchEvent', [evt] );    
    }

    // Class helper method for updating the thumbnail; and caching the selected file 
    public fileChange( fileInput: any ) {
        this.setFormField( "image", fileInput.target.files[0].name );
        this.imageFile = fileInput.target.files[0];

        let loader : Loading = this.loadingCtrl.create({
        content: "Loading..."
        });
        loader.present().then( () => {        
            let reader: FileReader = new FileReader();
            //let fileName : string = fileInput.target.files[0].name;

            reader.addEventListener( "load", (event) => {
                let fr : FileReader = <FileReader>event.target;
                this.imageUrl = fr.result;
                setTimeout( () => {
                    loader.dismiss();
                }, 500 );                
            });

            reader.addEventListener( "error", (event) => {
                console.log('file change error');
                loader.dismiss();
            });

            reader.readAsDataURL(fileInput.target.files[0]);            
        });
    }

    // Class method to close the modal view 
    public modalClose() {
        this.viewCtrl.dismiss();
    }

    public modalReset() {
        this.modalForm.reset( {
            image:      this.initPhoto.image,
            details:    this.initPhoto.notes,            
        });
    }

    public modalSubmit( formData : any, isValid : boolean ) {
        // Display a loader while the modal submits our changes
        let loader : Loading = this.loadingCtrl.create({
            content: this.editMode ? 'Updating...' : 'Adding...',
            dismissOnPageChange: true
        });
        loader.present().then( () => {
            // Determine what the submit button shoudl be doing based on the validity
            // of the form and the mode of the modal.
            if( isValid === true && !this.editMode) {
                // The submit button should save a new activity
                this.photoSvc.addPhoto( this.imageFile, formData.details, this.reportSvc.getActiveReportId() )
                .subscribe( result => {
                    this.eventSystem.publish('picture:added', result );
                }, err => {
                    // Operation failed; dismiss loader and display an error message
                    loader.dismiss().then( () => {
                        this.displayError("There was an error adding this picture to the report.  Please try again later." );
                    });
                }, () => {
                    // Operation successful; close modal
                    this.modalClose();
                });
            } else if( isValid === true && this.editMode ) {
                // The submit button should save changes to an existing activity
                this.photoSvc.updatePhoto( this.initPhoto.id, formData.details, this.reportSvc.getActiveReportId() )
                .subscribe( result => {
                    this.eventSystem.publish('picture:updated', result );
                }, err => {
                    // Operation failed; dismiss loader and display an error message
                    loader.dismiss().then( () => {
                        this.displayError("There was an error updating this picture.  Please try again later." );
                    });
                }, () => {
                    // Operation successful; close modal
                    this.modalClose();
                }); 
            }
        });
    }

    public modalDelete() {
        // Verify that we are in the correct editMode and that our id is valid
        if( this.editMode && this.initPhoto.id > 0 )
        {
            // Activity id is valid; instantiate delete confirmation
            this.displayConfirmation( 'Delete Picture', 'Are you sure you want to delete this picture from this report?', () => 
            {
                // User confirmed- so let's initiate the delete process
                let loader : Loading = this.loadingCtrl.create( {
                    content: 'Deleting...'
                });
                loader.present().then( () => {
                    // Data hNow let's start our delete process 
                    this.photoSvc.deletePhoto( this.initPhoto.id, this.reportSvc.getActiveReportId() ).subscribe( result => {
                        // Data has been received from our service 
                        this.eventSystem.publish('picture:deleted', this.initPhoto );
                    }, err => {
                        // Operation failed; dismiss loader and shown an error message 
                        loader.dismiss().then( () => {
                            this.displayError("There was an error deleting this picture.  Please try again later.");
                        });
                    }, () => {
                        // Operation succeeded, dismiss loader
                        this.viewCtrl.dismiss().then( () => {
                            loader.dismiss();
                        });
                    });
                });
            });
        } else {
            // The activity id supplied is not valid; display a generic error 
            this.displayError( 'An error has occurred.  Please try again later.' );
        }
    }
}



// WEBPACK FOOTER //
// ./src/pages/report/tab-main/picture/picture-modal/picture-modal.component.ts